import styled from "@emotion/styled";
import { useRive } from "@rive-app/react-canvas";
import React from "react";
import { useInView } from "react-intersection-observer";

import { IBynderRiveAnimation } from "@brexhq/shared/types/BynderAsset";

import { Container } from "../../layout/Container/Container";
import { PlaybackButton } from "../../modules/Carousel/components/PlaybackButton";

import { StyledRiveAnimationProps } from "./types";

const Fallback = styled.img<{ aspectRatio?: number }>(({ aspectRatio }) => {
  return {
    width: "100%",
    ...(aspectRatio ? { aspectRatio: `1 /${aspectRatio}` } : {}),
  };
});

export const RiveAnimation = React.forwardRef<
  HTMLCanvasElement,
  StyledRiveAnimationProps & IBynderRiveAnimation
>(function RiveAnimation(
  {
    originalUrl: riveUrl,
    thumbnailUrl,
    aspectRatio,
    play,
    playbackButton,
    animationProgress,
    onProgressUpdate,
    handleSetActiveSlide,
  },
  ref,
) {
  const autoplay = play === "auto";
  const [animationLoaded, setAnimationLoaded] = React.useState(false);
  // we're really just counting the frames to force a re-render
  const [, setFrameCount] = React.useState(0);

  const { rive, RiveComponent } = useRive({
    stateMachines: undefined,
    src: riveUrl,
    autoplay: autoplay,
    onLoad: () => {
      setAnimationLoaded(true);
    },
    onLoadError: (error) =>
      console.error("[ERROR LOADING RIVE]", {
        errorType: error.type,
        errorData: error.data,
      }),
    onAdvance: () => {
      // only force re-render if we're keeping track of animation's progress
      playbackButton && setFrameCount((prev) => prev + 1);
    },
  });

  const [playbackState, setPlaybackState] = React.useState<
    "playing" | "paused"
  >(rive?.isPlaying ? "playing" : "paused");

  const [obsRef, isInView] = useInView({
    threshold: 0.2,
  });

  const getAspectRatio = () => {
    if (aspectRatio) return 1 / aspectRatio;
    if (rive?.bounds) return rive.bounds.maxX / rive.bounds.maxY;
    return 9 / 16;
  };

  const aspectRatioValue = getAspectRatio();
  // @ts-expect-error - we're using private API in lack of better alternatives
  const time = rive?.animator?.animations[0]?.instance?.time;
  const [duration, setDuration] = React.useState<number | undefined>();

  React.useEffect(() => {
    // @ts-expect-error - we're using private API in lack of better alternatives
    if (!rive?.animator?.animations[0]?.animation.duration) return;
    // @ts-expect-error - we're using private API in lack of better alternatives
    setDuration(rive?.animator?.animations[0]?.animation.duration / 60);
  }, [rive]);

  // Pause animation when out of view
  React.useEffect(() => {
    if (!rive || !autoplay) return;
    if (isInView) {
      rive.play();
      setPlaybackState("playing");
    } else {
      rive.pause();
      setPlaybackState("paused");
    }
  }, [isInView, rive, autoplay]);

  React.useEffect(() => {
    if (!rive) return;
    if (rive.isPlaying) {
      setPlaybackState("playing");
    } else setPlaybackState("paused");
  }, [rive]);

  React.useEffect(() => {
    // Update animation progress
    if (!rive || !time || !duration || !animationProgress) return;
    onProgressUpdate && onProgressUpdate({ progress: time / duration });

    // Reset the animation when it's done
    // animationProgress doesn't reach 1, so we need to check if it's close enough
    if (1 - animationProgress.get() < 0.05) {
      rive?.reset();
      handleSetActiveSlide && handleSetActiveSlide();
    }
  }, [
    animationProgress,
    time,
    duration,
    handleSetActiveSlide,
    onProgressUpdate,
    rive,
  ]);

  const handleTogglePlayOrPause = React.useCallback(() => {
    if (rive) {
      if (rive.isPlaying) {
        rive.pause();
        setPlaybackState("paused");
      } else {
        rive.play();
        setPlaybackState("playing");
      }
    }
  }, [rive]);

  return (
    <Container ref={obsRef} position={"relative"} width={"100%"}>
      {!animationLoaded && (
        <Fallback src={thumbnailUrl} aspectRatio={aspectRatioValue} />
      )}
      {(isInView || animationLoaded) && (
        <RiveComponent
          ref={ref}
          style={{
            width: "100%",
            aspectRatio: aspectRatioValue,
            display: animationLoaded ? "block" : "none",
          }}
          {...(play === "onHover"
            ? {
                onMouseEnter: () => rive && rive.play(),
                onMouseLeave: () => rive && rive.pause(),
              }
            : {})}
        />
      )}
      {playbackButton && animationProgress && (
        <PlaybackButton
          progress={animationProgress}
          handleTogglePlayOrPause={handleTogglePlayOrPause}
          playbackState={playbackState}
        />
      )}
    </Container>
  );
});
